import { useParams } from "react-router-dom";
import {
    Box,
    Typography,
    List,
    ListItem,
    ListItemText,
    Divider,
    Button,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    DialogActions,
    TableContainer,
    Paper,
} from "@mui/material";
import { Print, Person as PersonIcon } from "@mui/icons-material";
import { useRef, useEffect, useState } from "react";
import { useReactToPrint } from "react-to-print";
import useAuthHeader from "react-auth-kit/hooks/useAuthHeader";
import { toast } from "react-toastify";
import { REACT_APP_BACKEND_URL } from "../../Utils/Language/urlBase";
import { formatDate } from "../../Utils/dateFormatter";

const CustomerDetails = () => {
    const { id } = useParams();
    const authHeader = useAuthHeader();

    const [customer, setCustomer] = useState({});
    const [invoices, setInvoices] = useState([]);
    const [totalRemaining, setTotalRemaining] = useState(0);
    const [payments, setPayments] = useState([]);
    const [loadingRecords, setLoadingRecords] = useState(true);
    const recordsRef = useRef(null);
    const reactToPrintFn = useReactToPrint({
        contentRef: recordsRef,
        documentTitle: `${customer?.name || "customer"}-records`,
        pageStyle: `
            @page { 
                size: auto;  
                margin: 15mm; 
            }
            @media print {
                body {
                    -webkit-print-color-adjust: exact;
                }
                /* Ensures header repeats on every page */
                thead {
                    display: table-header-group;
                }
                /* Ensures rows do not break aggressively */
                tr {
                    page-break-inside: avoid;
                }
            }
        `,
    });

    const [isPayDialogOpen, setIsPayDialogOpen] = useState(false);
    const [paymentAmount, setPaymentAmount] = useState(0);

    // Fetch customer details
    const fetchCustomerDetails = async () => {
        try {
            const response = await fetch(
                `${REACT_APP_BACKEND_URL}/otherparty/${id}`,
                {
                    headers: { Authorization: authHeader },
                },
            );
            if (response.ok) {
                const { otherparty } = await response.json();
                setCustomer(otherparty);
            }
        } catch (error) {
            toast.error("Error fetching customer details.");
        }
    };

    // Fetch invoices for this customer
    const fetchInvoices = async () => {
        try {
            const response = await fetch(
                `${REACT_APP_BACKEND_URL}/invoices?otherPartyId=${id}`,
                { headers: { Authorization: authHeader } },
            );
            if (response.ok) {
                const { data } = await response.json();
                setInvoices(data);

                // Compute unpaid total remaining amount
                const unpaid = data.filter(
                    (inv) => Number(inv.remainingAmount) > 0,
                );
                const total = unpaid.reduce(
                    (sum, invoice) => sum + Number(invoice.remainingAmount),
                    0,
                );
                setTotalRemaining(total);
                setLoadingRecords(false);
            }
        } catch (error) {
            toast.error("Error fetching unpaid invoices.");
            setLoadingRecords(false);
        }
    };

    // Fetch previous payments
    const fetchPayments = async () => {
        try {
            const response = await fetch(
                `${REACT_APP_BACKEND_URL}/payments/byOtherParty/${id}`,
                { headers: { Authorization: authHeader } },
            );
            if (response.ok) {
                const { payments } = await response.json();
                setPayments(payments);
                setLoadingRecords(false);
            }
        } catch (error) {
            toast.error("Error fetching payments.");
            setLoadingRecords(false);
        }
    };

    const handleSubmitPayment = async () => {
        console.log(paymentAmount);
        if (!paymentAmount || isNaN(paymentAmount) || paymentAmount <= 0) {
            toast.error("Please enter a valid payment amount.");
            return;
        }

        try {
            const response = await fetch(
                `${REACT_APP_BACKEND_URL}/payments/pay`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: authHeader,
                    },
                    body: JSON.stringify({
                        otherPartyId: id,
                        amount: parseFloat(paymentAmount),
                        currency: "USD",
                        rateAtPayment: 1.0,
                    }),
                },
            );
            if (response.ok) {
                toast.success("Payment applied successfully!");
                setIsPayDialogOpen(false);
                fetchInvoices();
                fetchPayments();
            } else {
                const errorData = await response.json();
                toast.error(errorData.message || "Error processing payment.");
            }
        } catch (error) {
            toast.error("Error submitting payment.");
        }
    };

    useEffect(() => {
        const fetchAll = async () => {
            setLoadingRecords(true);
            await Promise.all([
                fetchCustomerDetails(),
                fetchInvoices(),
                fetchPayments(),
            ]);
            setLoadingRecords(false);
        };
        fetchAll();
    }, []);

    // memoize combined records so we don't recompute every render
    const combinedRecords = (() => {
        const invoiceMap = {};
        invoices.forEach((inv) => {
            invoiceMap[inv.id] = inv;
        });
        const invoiceRows = invoices.map((inv) => ({
            id: `inv-${inv.id}`,
            date: new Date(inv.createdAt),
            type: "Invoice",
            reference: inv.serialId || inv.id,
            debit: Number(inv.priceFinal) || 0,
            credit: 0,
        }));
        const paymentRows = payments.map((p) => ({
            id: `pay-${p.id}`,
            date: new Date(p.createdAt),
            type: "Payment",
            reference: invoiceMap[p.invoiceId]?.serialId || p.invoiceId,
            debit: 0,
            credit: Number(p.amount) || 0,
        }));
        const combined = [...invoiceRows, ...paymentRows];
        // Sort chronologically: oldest first (top to bottom)
        const combinedAsc = combined.slice().sort((a, b) => a.date - b.date);

        // Calculate running balance down the ledger
        let cumulative = 0;
        combinedAsc.forEach((row) => {
            cumulative += row.debit;
            cumulative -= row.credit;
            row.cumulativeBalance = cumulative;
        });
        return combinedAsc;
    })();

    // Current overall balance (latest chronological record)
    const currentBalance =
        (combinedRecords.length &&
            combinedRecords[combinedRecords.length - 1].cumulativeBalance) ||
        0;

    const formatCurrency = (value) =>
        new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 4,
        }).format(value);

    return (
        <Box sx={{ p: 3, width: "100%" }}>
            {/* Modern Page Header */}
            <Paper
                elevation={0}
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                    p: 2.5,
                    borderRadius: 3,
                    background:
                        "linear-gradient(135deg, #0097a7 0%, #006064 100%)",
                    color: "white",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <PersonIcon sx={{ fontSize: 32, opacity: 0.9 }} />
                    <Box>
                        <Typography variant="h5" fontWeight={700}>
                            {customer.name}'s Details
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            {customer.phone || "No phone provided"}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                    <Box sx={{ textAlign: "right", mr: 2 }}>
                        <Typography
                            variant="caption"
                            sx={{ opacity: 0.8, display: "block" }}
                        >
                            Current Balance
                        </Typography>
                        <Typography variant="h6" fontWeight={700}>
                            {formatCurrency(currentBalance)}
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        onClick={() => setIsPayDialogOpen(true)}
                        sx={{
                            bgcolor: "rgba(255,255,255,0.2)",
                            color: "white",
                            border: "1.5px solid rgba(255,255,255,0.4)",
                            fontWeight: 700,
                            "&:hover": { bgcolor: "rgba(255,255,255,0.35)" },
                        }}
                    >
                        Pay Now
                    </Button>
                </Box>
            </Paper>

            {/* Account Statement */}
            <Paper sx={{ mb: 3, borderRadius: 2, overflow: "hidden" }}>
                <Box
                    sx={{
                        px: 2.5,
                        py: 2,
                        bgcolor: "grey.50",
                        borderBottom: "1px solid #e0e0e0",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <Typography variant="h6" fontWeight={700}>
                        📋 Account Statement
                    </Typography>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => reactToPrintFn?.()}
                        disabled={loadingRecords}
                        startIcon={<Print />}
                    >
                        Print Records
                    </Button>
                </Box>

                <Box p={0}>
                    <div ref={recordsRef}>
                        <TableContainer
                            sx={{ "@media print": { overflow: "visible" } }}
                        >
                            <Table size="small">
                                <TableHead>
                                    <TableRow
                                        sx={{
                                            bgcolor: "grey.100",
                                            "@media print": {
                                                bgcolor: "transparent",
                                                borderTop: "none",
                                            },
                                        }}
                                    >
                                        {/* Injecting the Header inside the first cell (or across all) for printing */}
                                        <TableCell
                                            colSpan={5}
                                            sx={{
                                                display: "none",
                                                "@media print": {
                                                    display: "table-cell",
                                                    padding: "0 0 20px 0",
                                                    borderBottom: "none",
                                                },
                                            }}
                                        >
                                            <Typography
                                                variant="h6"
                                                sx={{ fontWeight: 700 }}
                                            >
                                                Dannawi Med Lab
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ display: "block" }}
                                            >
                                                Customer: {customer?.name || ""}{" "}
                                                <br />
                                                Phone: {customer?.phone || ""}
                                            </Typography>
                                            <Divider sx={{ my: 1 }} />
                                        </TableCell>
                                    </TableRow>
                                    <TableRow sx={{ bgcolor: "grey.100" }}>
                                        <TableCell sx={{ fontWeight: 700 }}>
                                            Date
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>
                                            Ref/Description
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>
                                            Debit (Charges)
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>
                                            Credit (Payments)
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>
                                            Balance
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {/* Build combined list chronologically */}
                                    {(() => {
                                        // memoized combinedRecords computed above
                                        return combinedRecords.map((row) => (
                                            <TableRow key={row.id}>
                                                <TableCell>
                                                    {formatDate(row.date)}
                                                </TableCell>
                                                <TableCell>
                                                    {row.type} - {row.reference}
                                                </TableCell>
                                                <TableCell>
                                                    {row.debit > 0
                                                        ? formatCurrency(
                                                              row.debit,
                                                          )
                                                        : "-"}
                                                </TableCell>
                                                <TableCell>
                                                    {row.credit > 0
                                                        ? formatCurrency(
                                                              row.credit,
                                                          )
                                                        : "-"}
                                                </TableCell>
                                                <TableCell>
                                                    {formatCurrency(
                                                        row.cumulativeBalance ||
                                                            0,
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ));
                                    })()}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </div>
                </Box>
            </Paper>

            {/* Payment Dialog */}
            {/* <CustomFormDialog
                title="Make a Payment"
                fields={[
                    {
                        label: "Payment Amount",
                        name: "amount",
                        type: "number",
                        placeholder: "Enter Ammount",
                        required: true,
                        width: "98%",
                        onFieldChange: (allFields, value) => {
                            console.log(value);
                            setPaymentAmount(value);
                            console.log({ allFields });
                            return allFields;
                        },
                    },
                ]}
                open={isPayDialogOpen}
                onClose={() => setIsPayDialogOpen(false)}
                handleSubmitClick={handleSubmitPayment}
                formSubmitText="Submit Payment"
            /> */}

            <Dialog
                open={isPayDialogOpen}
                onClose={() => setIsPayDialogOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    <Typography variant="h6">Make a Payment</Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>
                        Enter the amount you wish to pay:
                    </Typography>
                    <TextField
                        label="Payment Amount"
                        type="number"
                        onChange={(e) => setPaymentAmount(e.target.value)}
                        value={paymentAmount}
                        fullWidth
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        type="button"
                        onClick={() => setIsPayDialogOpen(false)}
                        color="primary"
                        variant="outlined "
                        style={{ marginRight: 10 }}
                    >
                        Close
                    </Button>
                    <Button
                        type="button"
                        onClick={handleSubmitPayment}
                        color="primary"
                        variant="contained"
                    >
                        Submit Payment
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Payment History */}
            <Paper sx={{ mb: 3, borderRadius: 2, overflow: "hidden" }}>
                <Box
                    sx={{
                        px: 2.5,
                        py: 2,
                        bgcolor: "grey.50",
                        borderBottom: "1px solid #e0e0e0",
                    }}
                >
                    <Typography variant="h6" fontWeight={700}>
                        💳 Legacy Payment History
                    </Typography>
                </Box>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: "grey.100" }}>
                                <TableCell sx={{ fontWeight: 700 }}>
                                    Payment ID
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>
                                    Invoice ID
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>
                                    Amount
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>
                                    Date
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {payments.map((payment) => (
                                <TableRow key={payment.id}>
                                    <TableCell>{payment.id}</TableCell>
                                    <TableCell>
                                        {invoices.find(
                                            (inv) =>
                                                inv.id === payment.invoiceId,
                                        )?.serialId || payment.invoiceId}
                                    </TableCell>
                                    <TableCell>
                                        {formatCurrency(payment.amount)}
                                    </TableCell>
                                    <TableCell>
                                        {formatDate(payment.createdAt)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Box>
    );
};

export default CustomerDetails;
